/*
  assets/variant-description-short.js

  Keeps the "variant short description" in sync with the currently selected variant.

  Expected snippet markup inside/near <product-variants>:
    - <script type="application/json" data-variant-description-short> { "123": "Text", ... } </script>
    - <div class="variant-description-short" data-variant-description-short-output></div>

  Works with:
    - radio UI
    - select UI
    - quick view injection (MutationObserver)
*/

(() => {
  if (window.__bbVariantDescShortInit) return;
  window.__bbVariantDescShortInit = true;

  function safeJsonParse(str) {
    try { return JSON.parse(str); } catch (e) { return null; }
  }

  function getDescMap(pv) {
    const el = pv.querySelector('[data-variant-description-short]');
    if (!el) return null;
    const raw = String(el.textContent || '').trim();
    if (!raw) return null;
    const parsed = safeJsonParse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  }

  function getOutputEl(pv) {
    return pv.querySelector('[data-variant-description-short-output]');
  }

  function getVariantsArray(pv) {
    // first script[type="application/json"] inside product-variants is your variants JSON in this theme
    const scripts = pv.querySelectorAll('script[type="application/json"]');
    for (const s of scripts) {
      // skip the description map script
      if (s.hasAttribute('data-variant-description-short')) continue;
      const parsed = safeJsonParse(String(s.textContent || '').trim());
      if (Array.isArray(parsed)) return parsed;
    }
    return null;
  }

  function getSelectedOptions(pv) {
    // Supports your theme’s select UI: .product-variant-container
    const selects = pv.querySelectorAll('select.product-variant-container');
    if (selects.length) {
      return Array.from(selects).map(sel => sel.value);
    }

    // Supports radio UI: input.product-variant-value checked
    const radios = pv.querySelectorAll('input.product-variant-value[type="radio"]');
    if (radios.length) {
      const byName = new Map();
      radios.forEach(r => {
        if (r.checked) byName.set(r.name, r.value);
      });
      return Array.from(byName.values());
    }

    return [];
  }

  function resolveVariantId(pv) {
    // 1) If theme provides currentVariant, use it
    const cv = pv.currentVariant;
    if (cv && cv.id != null) return String(cv.id);

    // 2) Try hidden input "name=id" nearest product-form
    const nearestForm = pv.closest('product-page, product-quick-view, .product-quick-view-container, .site-box-content, body');
    if (nearestForm) {
      const hiddenId = nearestForm.querySelector('input[name="id"][value]');
      if (hiddenId && hiddenId.value) return String(hiddenId.value);
    }

    // 3) Compute from selected option values + variants JSON
    const variants = getVariantsArray(pv);
    const selected = getSelectedOptions(pv);
    if (!variants || !selected.length) return '';

    const match = variants.find(v => {
      if (!v) return false;
      // Shopify variant objects have option1/option2/option3
      const opts = [v.option1, v.option2, v.option3].filter(Boolean);
      if (opts.length !== selected.length) return false;
      return opts.every((opt, idx) => String(opt) === String(selected[idx]));
    });

    return match && match.id != null ? String(match.id) : '';
  }

  function update(pv) {
    const out = getOutputEl(pv);
    if (!out) return;

    const map = getDescMap(pv);
    if (!map) {
      out.textContent = '';
      return;
    }

    const id = resolveVariantId(pv);
    if (!id) {
      out.textContent = '';
      return;
    }

    const next = map[id];
    out.textContent = next == null ? '' : String(next);
  }

  function bindOne(pv) {
    if (!pv || pv.__bbVariantDescShortBound) return;
    pv.__bbVariantDescShortBound = true;

    // 1) Theme custom events (cover both common patterns)
    pv.addEventListener('VARIANT_CHANGE', () => update(pv));
    pv.addEventListener('variant:change', () => update(pv));
    pv.addEventListener('variant-change', () => update(pv));

    // 2) Native change events (selects + radios)
    pv.addEventListener('change', (e) => {
      const t = e.target;
      if (!t) return;
      if (
        t.matches('select.product-variant-container') ||
        t.matches('input.product-variant-value')
      ) {
        // allow theme to update hidden id/currentVariant first if it does
        requestAnimationFrame(() => update(pv));
      }
    });

    // Initial paint
    update(pv);
  }

  function bindAll(root = document) {
    root.querySelectorAll('product-variants').forEach(bindOne);
  }

  // Initial bind
  if (document.readyState !== 'loading') {
    bindAll(document);
  } else {
    document.addEventListener('DOMContentLoaded', () => bindAll(document));
  }

  // For quick-view + section injection
  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      if (m.addedNodes && m.addedNodes.length) {
        bindAll(document);
        break;
      }
    }
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
